import { createVuetify, type ThemeDefinition } from 'vuetify'
import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'

/**
 * Restrained Material Design 3 inspired palette with a Google blue accent on light
 * neutral surfaces. This is a productivity application, not an expressive one.
 */
const threadlineLightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    primary: '#1a73e8',
    'primary-darken-1': '#1557b0',
    secondary: '#5f6368',
    background: '#f1f3f4',
    surface: '#ffffff',
    'surface-variant': '#e8eaed',
    'on-surface-variant': '#3c4043',
    /**
     * The ground a message you sent is written on. It is a theme colour rather than a
     * literal so that the text on it can keep reading `on-surface`: a bubble hardcoded
     * light stayed light after dark, and everything written on it disappeared.
     */
    'outgoing-message': '#e8f0fe',
    'outgoing-message-border': '#d2e3fc',
    error: '#d93025',
    success: '#188038',
    warning: '#e37400',
    info: '#1a73e8',
  },
}

/**
 * The same restraint after dark: a dim neutral ground rather than pure black, surfaces that
 * lift off it, and a blue raised enough to stay legible on them. The accent is lightened
 * rather than reused — `#1a73e8` on a dark surface fails contrast, which is the whole
 * reason a dark theme is a palette of its own and not an inversion.
 */
const threadlineDarkTheme: ThemeDefinition = {
  dark: true,
  colors: {
    primary: '#8ab4f8',
    'primary-darken-1': '#669df6',
    secondary: '#9aa0a6',
    background: '#121316',
    surface: '#1e1f22',
    'surface-variant': '#303134',
    'on-surface-variant': '#e3e3e3',
    // Deep enough to read light text on, and blue enough to still say "you wrote this".
    'outgoing-message': '#1b3a5c',
    'outgoing-message-border': '#27507c',
    error: '#f28b82',
    success: '#81c995',
    warning: '#fdd663',
    info: '#8ab4f8',
  },
}

export const vuetify = createVuetify({
  theme: {
    // Which one is in force is decided at runtime from the device and the person's own
    // choice, in `useColorScheme`. Light is only where a tab starts before that is read.
    defaultTheme: 'threadlineLight',
    themes: {
      threadlineLight: threadlineLightTheme,
      threadlineDark: threadlineDarkTheme,
    },
  },
  defaults: {
    global: { rounded: 'lg' },
    VBtn: { variant: 'flat', rounded: 'lg' },
    VTextField: { variant: 'outlined', density: 'comfortable', color: 'primary' },
    VTextarea: { variant: 'outlined', density: 'comfortable', color: 'primary' },
    VSelect: { variant: 'outlined', density: 'comfortable', color: 'primary' },
    VCard: { elevation: 0, border: true },
    VList: { density: 'comfortable' },
  },
})
